import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class ExaPdpGalleryZoom extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.dialog = this.querySelector('[data-exa-zoom-dialog]');
    this.viewport = this.querySelector('[data-exa-zoom-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-exa-zoom-slide]'));
    this.previousButton = this.querySelector('[data-exa-zoom-previous]');
    this.nextButton = this.querySelector('[data-exa-zoom-next]');

    this.addEventListener('click', this.handleClick);
    this.dialog?.addEventListener('click', this.handleDialogClick);
    this.dialog?.addEventListener('close', this.handleDialogClose);
    this.viewport?.addEventListener('scroll', this.handleViewportScroll, { passive: true });
    this.viewport?.addEventListener('keydown', this.handleViewportKeydown);
    this.viewport?.addEventListener('pointerdown', this.handlePointerDown);
    this.viewport?.addEventListener('pointermove', this.handlePointerMove);
    this.viewport?.addEventListener('pointerup', this.handlePointerUp);
    this.viewport?.addEventListener('pointercancel', this.handlePointerUp);
    window.addEventListener('resize', this.handleResize);

    this.updateActiveSlide(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.handleClick);
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('close', this.handleDialogClose);
    this.viewport?.removeEventListener('scroll', this.handleViewportScroll);
    this.viewport?.removeEventListener('keydown', this.handleViewportKeydown);
    this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
    this.viewport?.removeEventListener('pointermove', this.handlePointerMove);
    this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
    this.viewport?.removeEventListener('pointercancel', this.handlePointerUp);
    window.removeEventListener('resize', this.handleResize);
    window.cancelAnimationFrame(this.scrollFrame);
    this.unlockPageScroll();
  }

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const closeButton = target.closest('[data-exa-zoom-close]');
    if (closeButton) {
      this.closeZoom();
      return;
    }

    if (target.closest('[data-exa-zoom-previous]')) {
      this.goToAdjacentSlide(-1);
      return;
    }

    if (target.closest('[data-exa-zoom-next]')) {
      this.goToAdjacentSlide(1);
      return;
    }

    const selector = target.closest('[data-exa-zoom-select], [data-exa-zoom-trigger]');
    if (!(selector instanceof HTMLElement)) return;

    const index = Number(selector.dataset.exaZoomIndex);
    if (Number.isNaN(index)) return;

    event.preventDefault();

    if (selector.matches('[data-exa-zoom-trigger]')) {
      this.openZoom(index, selector);
      return;
    }

    this.goToSlide(index);
  };

  handleDialogClick = (event) => {
    if (event.target === event.currentTarget) {
      this.closeZoom();
    }
  };

  handleDialogClose = () => {
    this.unlockPageScroll();
    const openedBy = this.openedBy;
    this.openedBy = undefined;
    window.requestAnimationFrame(() => openedBy?.focus({ preventScroll: true }));
  };

  handleViewportScroll = () => {
    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.updateActiveSlide(this.getClosestSlidePosition());
    });
  };

  handleViewportKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToAdjacentSlide(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToAdjacentSlide(1);
    }
  };

  handlePointerDown = (event) => {
    if (!(this.viewport instanceof HTMLElement)) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.isPrimary === false) return;

    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragCurrentX = event.clientX;
    this.dragCurrentY = event.clientY;
    this.dragStartScrollLeft = this.viewport.scrollLeft;
    this.dragStartPosition = this.activeSlidePosition ?? this.getClosestSlidePosition();
    this.dragPointerId = event.pointerId;
    this.isHorizontalDrag = event.pointerType === 'mouse';

    this.viewport.classList.toggle('is-dragging', this.isHorizontalDrag);
    this.viewport.setPointerCapture(event.pointerId);
  };

  handlePointerMove = (event) => {
    if (
      !(this.viewport instanceof HTMLElement) ||
      this.dragStartX === undefined ||
      this.dragPointerId !== event.pointerId
    ) {
      return;
    }

    this.dragCurrentX = event.clientX;
    this.dragCurrentY = event.clientY;

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;
    const absoluteDeltaX = Math.abs(deltaX);
    const absoluteDeltaY = Math.abs(deltaY);

    if (!this.isHorizontalDrag) {
      if (absoluteDeltaX < 8 && absoluteDeltaY < 8) return;

      this.isHorizontalDrag = absoluteDeltaX > absoluteDeltaY;

      if (this.isHorizontalDrag) {
        this.viewport.classList.add('is-dragging');
      }
    }

    if (!this.isHorizontalDrag) return;

    event.preventDefault();
    this.viewport.scrollLeft = this.dragStartScrollLeft - deltaX;
  };

  handlePointerUp = (event) => {
    if (
      !(this.viewport instanceof HTMLElement) ||
      this.dragStartX === undefined ||
      this.dragPointerId !== event.pointerId
    ) {
      return;
    }

    const deltaX = (this.dragCurrentX ?? event.clientX) - this.dragStartX;
    const deltaY = (this.dragCurrentY ?? event.clientY) - this.dragStartY;
    const absoluteDeltaX = Math.abs(deltaX);
    const absoluteDeltaY = Math.abs(deltaY);
    const swipeThreshold = Math.min(80, Math.max(32, this.viewport.clientWidth * 0.12));
    const shouldAdvanceSlide =
      event.type !== 'pointercancel' &&
      this.isHorizontalDrag &&
      absoluteDeltaX >= swipeThreshold &&
      absoluteDeltaX > absoluteDeltaY;
    const targetPosition = shouldAdvanceSlide
      ? this.dragStartPosition + (deltaX < 0 ? 1 : -1)
      : this.getClosestSlidePosition();

    this.resetDragState(event.pointerId);
    this.goToSlidePosition(targetPosition);
  };

  resetDragState(pointerId) {
    if (!(this.viewport instanceof HTMLElement)) return;

    this.dragStartX = undefined;
    this.dragStartY = undefined;
    this.dragCurrentX = undefined;
    this.dragCurrentY = undefined;
    this.dragStartScrollLeft = undefined;
    this.dragStartPosition = undefined;
    this.dragPointerId = undefined;
    this.isHorizontalDrag = undefined;
    this.viewport.classList.remove('is-dragging');

    if (this.viewport.hasPointerCapture(pointerId)) {
      this.viewport.releasePointerCapture(pointerId);
    }
  }

  handleResize = () => {
    this.goToSlidePosition(this.activeSlidePosition ?? 0, false);
  };

  openZoom(index, trigger) {
    if (!(this.dialog instanceof HTMLDialogElement) || !(this.viewport instanceof HTMLElement)) return;

    if (!this.dialog.open) {
      this.openedBy = trigger;
      this.lockPageScroll();
      this.dialog.showModal();
    }

    window.requestAnimationFrame(() => {
      this.goToSlide(index, false);
      this.viewport?.focus({ preventScroll: true });
    });
  }

  closeZoom() {
    if (this.dialog instanceof HTMLDialogElement && this.dialog.open) {
      this.dialog.close();
    }
  }

  goToAdjacentSlide(direction) {
    this.goToSlidePosition((this.activeSlidePosition ?? 0) + direction);
  }

  goToSlide(index, smooth = true) {
    const position = this.slides.findIndex((slide) => Number(slide.dataset.exaZoomIndex) === index);
    if (position === -1) return;

    this.goToSlidePosition(position, smooth);
  }

  goToSlidePosition(position, smooth = true) {
    if (!(this.viewport instanceof HTMLElement)) return;

    const nextPosition = Math.max(0, Math.min(position, this.slides.length - 1));
    const slide = this.slides[nextPosition];
    if (!(slide instanceof HTMLElement)) return;

    this.viewport.scrollTo({
      left: slide.offsetLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
    this.updateActiveSlide(nextPosition);
  }

  getClosestSlidePosition() {
    if (!(this.viewport instanceof HTMLElement)) return 0;

    return this.slides.reduce((closest, slide, index) => {
      const currentDistance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
      const closestDistance = Math.abs(this.slides[closest].offsetLeft - this.viewport.scrollLeft);

      return currentDistance < closestDistance ? index : closest;
    }, 0);
  }

  updateActiveSlide(position) {
    const activeSlide = this.slides[position];
    if (!(activeSlide instanceof HTMLElement)) return;

    this.activeSlidePosition = position;
    const activeIndex = Number(activeSlide.dataset.exaZoomIndex);

    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== position));
    });

    this.querySelectorAll('[data-exa-zoom-select]').forEach((selector) => {
      if (!(selector instanceof HTMLElement)) return;
      selector.setAttribute('aria-selected', String(Number(selector.dataset.exaZoomIndex) === activeIndex));
    });

    if (this.previousButton instanceof HTMLButtonElement) {
      this.previousButton.disabled = position === 0;
    }

    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = position === this.slides.length - 1;
    }
  }

  lockPageScroll() {
    this.previousDocumentOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlockPageScroll() {
    if (this.previousDocumentOverflow === undefined) return;

    document.documentElement.style.overflow = this.previousDocumentOverflow;
    this.previousDocumentOverflow = undefined;
  }
}

class ExaPdpVariantDetails extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.updateVariantDetails);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closest('.shopify-section')?.removeEventListener(ThemeEvents.variantUpdate, this.updateVariantDetails);
  }

  updateVariantDetails = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) return;

    const html = event.detail?.data?.html;
    if (!html) return;

    this.querySelectorAll('[data-exa-variant-dynamic]').forEach((target) => {
      if (!(target instanceof HTMLElement)) return;

      const key = target.dataset.exaVariantDynamic;
      const next = html.querySelector(`[data-exa-variant-dynamic="${key}"]`);
      if (next instanceof HTMLElement) {
        target.replaceChildren(...Array.from(next.childNodes).map((node) => node.cloneNode(true)));
      }
    });
  };
}

class ExaPdpCompleteLook extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.track = this.querySelector('[data-exa-complete-track]');
    this.previousButton = this.querySelector('[data-exa-complete-prev]');
    this.nextButton = this.querySelector('[data-exa-complete-next]');

    this.previousButton?.addEventListener('click', this.handlePreviousClick);
    this.nextButton?.addEventListener('click', this.handleNextClick);
    this.track?.addEventListener('scroll', this.updateControls, { passive: true });
    window.addEventListener('resize', this.updateControls);
    this.updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.previousButton?.removeEventListener('click', this.handlePreviousClick);
    this.nextButton?.removeEventListener('click', this.handleNextClick);
    this.track?.removeEventListener('scroll', this.updateControls);
    window.removeEventListener('resize', this.updateControls);
  }

  handlePreviousClick = () => {
    this.scrollByCard(-1);
  };

  handleNextClick = () => {
    this.scrollByCard(1);
  };

  scrollByCard(direction) {
    if (!(this.track instanceof HTMLElement)) return;

    const item = this.track.querySelector('.exa-pdp-info__complete-item');
    if (!(item instanceof HTMLElement)) return;

    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const step = item.getBoundingClientRect().width + gap;

    this.track.scrollBy({
      left: direction * step,
      behavior: 'smooth',
    });
  }

  updateControls = () => {
    if (!(this.track instanceof HTMLElement)) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;

    if (this.previousButton instanceof HTMLButtonElement) {
      this.previousButton.disabled = atStart;
    }

    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = atEnd || maxScroll <= 1;
    }
  };
}

const enhanceLinkedOptionAccessibility = (root = document) => {
  root.querySelectorAll('.exa-pdp-info__variant-picker .lv-option-item[href]').forEach((link) => {
    if (!(link instanceof HTMLElement)) return;
    if (link.getAttribute('aria-label')?.trim()) return;

    const label = link.dataset.value?.trim() || link.textContent.trim() || link.getAttribute('title')?.trim();
    if (!label) return;

    link.setAttribute('aria-label', label);
    link.setAttribute('title', label);
  });
};

if (!customElements.get('exa-pdp-variant-details')) {
  customElements.define('exa-pdp-variant-details', ExaPdpVariantDetails);
}

if (!customElements.get('exa-pdp-gallery-zoom')) {
  customElements.define('exa-pdp-gallery-zoom', ExaPdpGalleryZoom);
}

if (!customElements.get('exa-pdp-complete-look')) {
  customElements.define('exa-pdp-complete-look', ExaPdpCompleteLook);
}

enhanceLinkedOptionAccessibility();

const linkedOptionsRoot = document.querySelector('.exa-pdp-info__variant-picker');
if (linkedOptionsRoot) {
  const linkedOptionsObserver = new MutationObserver(() => enhanceLinkedOptionAccessibility(linkedOptionsRoot));
  linkedOptionsObserver.observe(linkedOptionsRoot, { childList: true, subtree: true });
}
